/**
 * Dashboard Persistence API
 */
import { api } from '@/lib/api';
import { edenError } from '@solidafy/api-client';
import type {
  DashboardListItem,
  DashboardFull,
  UpdateDashboardBody,
  DashboardFavorite,
  ToggleFavoriteResponse,
  SetDefaultDashboardResponse,
} from '@solidafy/api-client';
import type { DashboardSpec } from '@solidafy/bi-core';

export async function listDashboards(): Promise<DashboardListItem[]> {
  const { data, error } = await api.api.v1.bi.dashboards.get();
  if (error) {
    throw new Error(edenError(error, 'Failed to list dashboards'));
  }
  return data;
}

export async function getDashboard(id: string): Promise<DashboardFull> {
  const { data, error } = await api.api.v1.bi.dashboards({ id }).get();
  if (error) {
    throw new Error(edenError(error, 'Failed to get dashboard'));
  }
  return data;
}

export async function createDashboard(request: {
  name: string;
  description?: string;
  spec: DashboardSpec;
}): Promise<DashboardFull> {
  const { data, error } = await api.api.v1.bi.dashboards.post({
    name: request.name,
    description: request.description,
    // DashboardSpec → Record<string, unknown> for the API
    spec: request.spec,
  });
  if (error) {
    throw new Error(edenError(error, 'Failed to create dashboard'));
  }
  if (!data || !('id' in data)) {
    throw new Error('Failed to create dashboard');
  }
  return data;
}

export async function updateDashboard(
  id: string,
  request: { name?: string; description?: string; spec?: DashboardSpec; is_published?: boolean },
): Promise<DashboardFull> {
  const body: UpdateDashboardBody = {
    name: request.name,
    description: request.description,
    is_published: request.is_published,
  };
  if (request.spec) {
    // DashboardSpec → Record<string, unknown> for the API
    body.spec = request.spec;
  }
  const { data, error } = await api.api.v1.bi.dashboards({ id }).put(body);
  if (error) {
    throw new Error(edenError(error, 'Failed to update dashboard'));
  }
  if (!data || !('id' in data)) {
    throw new Error('Failed to update dashboard');
  }
  return data;
}

export async function deleteDashboard(id: string): Promise<void> {
  const { error } = await api.api.v1.bi.dashboards({ id }).delete();
  if (error) {
    throw new Error(edenError(error, 'Failed to delete dashboard'));
  }
}

// ── Favorites ──

export type { DashboardFavorite };

export async function listFavorites(): Promise<DashboardFavorite[]> {
  const { data, error } = await api.api.v1.bi.favorites.get();
  if (error) {
    return [];
  }
  return data;
}

export async function toggleFavorite(dashboardId: string): Promise<ToggleFavoriteResponse> {
  const { data, error } = await api.api.v1.bi.dashboards({ id: dashboardId }).favorite.post();
  if (error) {
    throw new Error(edenError(error, 'Failed to toggle favorite'));
  }
  return data;
}

export async function setDefaultDashboard(dashboardId: string | null): Promise<SetDefaultDashboardResponse> {
  const { data, error } = await api.api.v1.bi.dashboards.default.post({ dashboard_id: dashboardId });
  if (error) {
    throw new Error(edenError(error, 'Failed to set default dashboard'));
  }
  return data;
}
